import {
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Header,
  Icon,
  Select,
  Separator,
  Skeleton,
  styled,
  Text,
  Tooltip,
} from '@driftt/dds'
import { faCircleInfo } from '@driftt/dds.icons/solid/faCircleInfo'
import { Breadcrumbs } from '@driftt/tide-core'
import { Subnav } from '@driftt/tide-subnav'
import { showTransientNotification } from '@driftt/traffic-control'
import React, { useCallback, useEffect } from 'react'
import { useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'

import {
  archiveScenario,
  createScenario,
  gerReducedScenarios,
  getActiveCustomScenariosCount,
  getScenario,
  restoreScenario,
  Scenario,
  updateScenario,
} from 'api/scenarios'
import ConfirmationModal from 'components/ScenariosTable/ConfirmationModal'
import TrainingModal from 'components/ScenariosTable/TrainingInfoModal'
import { APP_PAGE_TITLES } from 'constants/pageTitles'
import { MAX_ACTIVE_SCENARIOS, SCENARIO_TEMPLATES } from 'constants/scenarios'
import useDocumentTitle from 'hooks/usePageTitle'
import useScenarioExecutionsWithData from 'hooks/useScenariosTriggeringPhrases'
import { userHasAnyPlaybookAccess } from 'utils/access'
import { getSubNavNodes } from 'utils/getSubNavNodes'
import { isEmptyOrNil } from 'utils/index'

import HeaderInlineInput from './HeaderInlineInput'
import SamplePhrases from './SamplePhrases'
import SaveConfirmationModal from './SaveConfirmationModal'
import StaticInput from './StaticInput'
import ValidationModal, { ValidationObject } from './ValidationModal'

interface ConfirmationModalOptions {
  scenarioId: string
  action: 'archive' | 'restore'
}

interface EditorRoutingProps extends RouteComponentProps {
  match: {
    params: {
      id?: string
      templateId?: string
    }
    isExact: boolean
    path: string
    url: string
  }
  isNewScenario: boolean
}

const BREADCRUMB_HEADINGS = [
  {
    title: 'Chat scenarios',
    link: '/playbooks/ai-scenarios',
  },
  {
    title: 'Edit scenario',
    link: '/',
    isActive: true,
  },
]

const CustomCard = styled(Card, {
  width: '50vw',
  maxWidth: '722px',
  marginBottom: '$2',
})

const OFFRAMP_OPTIONS = [
  {
    label: 'Sales offramp',
    value: 'SALES',
  },
  {
    label: 'Support offramp',
    value: 'SUPPORT',
  },
]

const DEFAULT_EXIT_QUESTIONS = {
  SALES: `Looks like you're interested in learning more. Would you like me to connect you with someone in sales?`,
  SUPPORT: `Looks like you're in need of support assistance. Would you like me to connect you with one of our support options?`,
}

const ScenariosEditor = ({
  match,
  isNewScenario: isNew,
}: EditorRoutingProps) => {
  const [isNewScenario, setIsNewScenario] = useState(isNew)
  const [scenario, setScenario] = useState<Scenario | undefined>()
  const [editedScenario, setEditedScenario] = useState<
    Partial<Scenario> | undefined
  >()
  const [confirmationModalOptions, setConfirmationModalOptions] = useState<
    ConfirmationModalOptions | undefined
  >(undefined)
  const [
    isDescriptionValidationModalOpen,
    setIsDescriptionValidationModalOpen,
  ] = useState(false)
  const [
    isInstructionsValidationModalOpen,
    setIsInstructionsValidationModalOpen,
  ] = useState(false)
  const [isExitValidationModalOpen, setIsExitValidationModalOpen] =
    useState(false)
  const [isSaveConfirmationModalOpen, setIsSaveConfirmationModalOpen] =
    useState(false)

  const [sidebarIsOpen, setSidebarIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const [currentActiveScenariosCount, setCurrentActiveScenariosCount] =
    useState(0)
  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()
  useDocumentTitle(APP_PAGE_TITLES.SCENARIO)

  const SUB_NAV_NODES = getSubNavNodes()
  const orgId: string = useSelector(({ user }: RootStateOrAny) => user.orgId)

  const { id = '', templateId } = match.params
  const [scenarioId, setScenarioId] = useState(id)

  const dispatch = useDispatch()
  const [enableFetchScenarios, setEnableFetchScenarios] = useState(true)

  const [templateDescription, setTemplateDescription] = useState<
    ValidationObject | undefined
  >()
  useEffect(() => {
    if (!isNewScenario || !templateId) return

    const template = SCENARIO_TEMPLATES.find(
      (template) => template.id === templateId
    )
    if (template && !template.isEmpty) {
      const { name, prompt = '', scenarioExamples, id } = template
      setEditedScenario({ name, prompt, scenarioExamples, templateId: id })
      setTemplateDescription({
        prompt,
        examples: scenarioExamples,
        response: '',
      })
    }
  }, [isNewScenario, templateId])

  useEffect(() => {
    async function fetchActiveScenariosCount() {
      try {
        const { data: count } = await getActiveCustomScenariosCount({ orgId })
        setCurrentActiveScenariosCount(count)
      } catch (e) {
        console.warn(e)
      }
    }

    fetchActiveScenariosCount()
  }, [orgId, scenario, isSaveConfirmationModalOpen])

  useEffect(() => {
    async function fetchScenario() {
      setIsLoading(true)
      try {
        const { data: scenarioData } = await getScenario({ scenarioId, orgId })
        setScenario(scenarioData)
        setIsLoading(false)
      } catch (e) {
        console.warn(e)
        showTransientNotification({
          type: 'error',
          message: 'We could not locate scenario.',
        })
        setHasFailed(true)
        setIsLoading(false)
      }
    }

    if (isNewScenario) return

    if (isEmptyOrNil(scenario) && !isLoading) {
      fetchScenario()
    }
  }, [scenarioId, orgId, scenario, isNewScenario, isLoading])

  const isAtScenariosLimit = currentActiveScenariosCount >= MAX_ACTIVE_SCENARIOS

  const onEditStringProperty =
    (property: keyof Scenario) => (value: string) => {
      if (value === scenario?.[property]) {
        const newEditedScenario = { ...editedScenario }
        delete newEditedScenario[property]
        setEditedScenario(newEditedScenario)
      } else {
        setEditedScenario({ ...editedScenario, [property]: value })
      }
    }

  const onToggleBooleanProperty =
    (property: keyof Scenario) => (checked: boolean) => {
      setEditedScenario({ ...editedScenario, [property]: checked })
    }

  const onSelectChange = (property: keyof Scenario) => (value: string) => {
    const newEditedScenario = { ...editedScenario, [property]: value }
    if (property === 'exit') {
      if (
        !editedScenario?.exitQuestion ||
        Object.values(DEFAULT_EXIT_QUESTIONS).includes(
          editedScenario.exitQuestion
        )
      ) {
        newEditedScenario.exitQuestion =
          DEFAULT_EXIT_QUESTIONS[value as 'SALES' | 'SUPPORT']
      }
    }
    setEditedScenario(newEditedScenario)
  }

  const onEditDescription = (prompt?: string, scenarioExamples?: string[]) => {
    setEditedScenario({ ...editedScenario, prompt, scenarioExamples })
  }

  const onEditInstruction = (
    prompt?: string,
    phrases?: string[],
    replyGuidelines?: string
  ) => {
    setEditedScenario({
      ...editedScenario,
      instruction: replyGuidelines || prompt,
    })
  }
  const onSaveNew = useMutation(createScenario, {
    onSuccess: ({ data: newScenarioId }) => {
      setScenarioId(newScenarioId)
      setIsNewScenario(false)
      setEditedScenario({})
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Scenario saved successfully`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not save scenario`,
        })
      )
    },
  })

  const onSaveScenario = useMutation(updateScenario, {
    onSuccess: ({ data: newScenarioId }) => {
      setScenarioId(newScenarioId)
      setEditedScenario({})
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Scenario saved successfully`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not save scenario`,
        })
      )
    },
  })

  const payload = {
    scenarioId,
    ...scenario,
    ...editedScenario,
  }

  const onSave = async () => {
    setIsSaveConfirmationModalOpen(false)
    const saveFunction =
      isNewScenario && !scenario
        ? onSaveNew.mutateAsync
        : onSaveScenario.mutateAsync
    await saveFunction({ orgId, payload })
    if (window) window.location.pathname = '/playbooks/ai-scenarios'
  }

  const onArchive = useMutation(archiveScenario, {
    onSuccess: () => {
      setScenario(undefined)
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully archived scenario`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not archive scenario`,
        })
      )
    },
  })

  const { data: allPossibleScenarios } = useQuery({
    queryKey: ['allPossibleScenarios'],
    queryFn: () =>
      gerReducedScenarios().then(({ data }) => {
        setEnableFetchScenarios(false)
        return data
      }),
    enabled: enableFetchScenarios,
  })

  const onRestore = useMutation(restoreScenario, {
    onSuccess: () => {
      setScenario(undefined)
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully restored scenario`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not restore scenario`,
        })
      )
    },
  })

  const ACTIONS = {
    archive: onArchive.mutate,
    restore: onRestore.mutate,
  }

  const confirmationModalAction = () => {
    if (!confirmationModalOptions) return
    const { scenarioId, action } = confirmationModalOptions
    ACTIONS[action]({ scenarioId, orgId })
    setConfirmationModalOptions(undefined)
  }

  const descriptionDisabled =
    !isNewScenario || scenario?.driftManaged || scenario?.archived
  const descriptionValue = editedScenario?.prompt || scenario?.prompt
  const instructionValue = editedScenario?.instruction || scenario?.instruction
  const useInstructionValue =
    typeof editedScenario?.useInstruction === 'undefined'
      ? scenario?.useInstruction
      : editedScenario?.useInstruction
  const exitQuestionValue =
    editedScenario?.exitQuestion || scenario?.exitQuestion
  const useExitValue =
    typeof editedScenario?.useExit === 'undefined'
      ? scenario?.useExit
      : editedScenario?.useExit
  const exitValue = editedScenario?.exit || scenario?.exit
  const examplesValue =
    editedScenario?.scenarioExamples || scenario?.scenarioExamples

  const checkForNameConflict = useCallback(() => {
    if (!payload || !payload.name || !Array.isArray(allPossibleScenarios)) {
      return false
    }
    const conflictScenario = allPossibleScenarios.find(
      (possibleScenario) =>
        possibleScenario &&
        possibleScenario.name &&
        possibleScenario.name.toLowerCase() === payload?.name?.toLowerCase() &&
        possibleScenario.scenarioId !== payload?.scenarioId
    )
    return conflictScenario !== undefined
  }, [payload, allPossibleScenarios])

  const {
    scenarioExecutions,
    isLoading: scenarioExecutionsIsLoading,
    isError,
  } = useScenarioExecutionsWithData(scenarioId)

  let descriptionTooltip
  if (!isNewScenario) {
    descriptionTooltip =
      'Your chatbot is being trained on this description, therefore it is no longer editable. To help your chatbot detect this scenario in future conversations, rate bot responses on the AI responses testing and Chat data pages.'
  }
  if (scenario?.driftManaged) {
    descriptionTooltip =
      'Descriptions for Drift-maintained scenarios are not customizable.'
  }

  const [saveButtonTooltip, setSaveButtonTooltip] = useState<
    string | undefined
  >()

  useEffect(() => {
    const getSaveButtonTooltip = () => {
      if (isEmptyOrNil(editedScenario)) {
        return 'No changes to save.'
      }
      if (checkForNameConflict()) {
        return 'Please rename this scenario to avoid a naming conflict with an existing one'
      }

      if (!payload?.name) {
        return 'Please create a name for this scenario.'
      }

      if (!payload?.prompt) {
        return 'Scenario description is required.'
      }

      if (payload?.useInstruction && !payload?.instruction) {
        return 'Please enter reply guidelines.'
      }

      if (payload?.useExit && !payload?.exit) {
        return 'Please select exit offramp.'
      }

      if (payload?.useExit && !payload?.exitQuestion) {
        return 'Please enter an exit confirmation question.'
      }

      if (!payload?.useExit && !payload?.useInstruction) {
        return 'Please select at least one action.'
      }
    }

    const tooltipMessage = getSaveButtonTooltip()
    setSaveButtonTooltip(tooltipMessage)
  }, [editedScenario, payload, checkForNameConflict])

  const isSavingChanges = onSaveNew.isLoading || onSaveScenario.isLoading

  const isSaveButtonDisabled =
    isEmptyOrNil(editedScenario) ||
    !payload?.name ||
    !payload?.prompt ||
    (payload?.useInstruction && !payload?.instruction) ||
    (payload?.useExit && (!payload?.exitQuestion || !payload?.exit)) ||
    (!payload?.useExit && !payload?.useInstruction) ||
    checkForNameConflict()

  return (
    <>
      {hasAnyPlaybookAccess && (
        <Subnav
          title="Chatbot"
          nodes={SUB_NAV_NODES}
          open={sidebarIsOpen}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
      )}
      <Flex direction="column" gap={0} fullWidth fullHeight>
        <TrainingModal />
        <Box
          fullWidth
          css={{ padding: '$1 $6', borderBottom: '$gray3 1px solid' }}
        >
          <Flex fullWidth justifyContent="space-between" alignItems="center">
            <Header>
              <Breadcrumbs headings={BREADCRUMB_HEADINGS} />
              <HeaderInlineInput
                placeholder="New scenario"
                value={editedScenario?.name || scenario?.name}
                disabled={scenario?.driftManaged}
                onChange={onEditStringProperty('name')}
              />
            </Header>
            <Flex direction="row">
              {scenario && (
                <Tooltip
                  content={
                    scenario.archived &&
                    isAtScenariosLimit &&
                    !scenario.driftManaged &&
                    `You’ve reached your max of 3 scenarios. Please archive a custom scenario in order to restore this one.`
                  }
                  side="left"
                >
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setConfirmationModalOptions({
                        scenarioId,
                        action: scenario.archived ? 'restore' : 'archive',
                      })
                    }
                    css={{ marginRight: '$half' }}
                    disabled={
                      scenario.archived &&
                      isAtScenariosLimit &&
                      !scenario.driftManaged
                    }
                  >
                    {scenario.archived ? 'Restore' : 'Archive'}
                  </Button>
                </Tooltip>
              )}
              <Tooltip content={saveButtonTooltip} side="left">
                <Button
                  loading={isSavingChanges}
                  onClick={() =>
                    isNewScenario
                      ? setIsSaveConfirmationModalOpen(true)
                      : onSave()
                  }
                  disabled={isSaveButtonDisabled}
                >
                  Save & close
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        </Box>
        <Flex
          fullWidth
          height="100%"
          direction="column"
          alignItems="center"
          css={{ background: '$gray2', padding: '$3 $6', overflowY: 'auto' }}
        >
          {hasFailed && <></>}
          {isLoading && <></>}
          {!hasFailed && !isLoading && (
            <>
              <CustomCard>
                <Card.Header>
                  <Header variant="h2" css={{ marginBottom: '$1' }}>
                    When site visitors talk about {scenario?.name}...
                  </Header>
                  <Separator orientation="horizontal" />
                </Card.Header>
                <Card.Content>
                  <Text>
                    Description
                    {scenario?.driftManaged && ' (Maintained by Drift)'}
                  </Text>
                  <StaticInput
                    disabled={descriptionDisabled}
                    value={descriptionValue}
                    placeholder="Provide a description that'll identify this scenario in conversation"
                    onClick={() => setIsDescriptionValidationModalOpen(true)}
                    tooltipCaption={descriptionTooltip}
                  />
                  {examplesValue &&
                    (examplesValue.length > 0 ||
                      scenarioExecutions.length > 0) && (
                      <Skeleton
                        isLoading={scenarioExecutionsIsLoading}
                        height={'150px'}
                      >
                        <SamplePhrases
                          isAIGenerated={
                            isError || isEmptyOrNil(scenarioExecutions)
                          }
                          phrases={scenarioExecutions}
                          aiGeneratedPhrases={examplesValue}
                        />
                      </Skeleton>
                    )}
                </Card.Content>
              </CustomCard>
              <CustomCard>
                <Card.Header>
                  <Header variant="h2" css={{ marginBottom: '$1' }}>
                    Take the following actions:
                  </Header>
                  <Separator orientation="horizontal" />
                </Card.Header>
                <Card.Content>
                  <Flex direction="column" gap={1}>
                    <Checkbox
                      checked={useInstructionValue}
                      label="Reply using the following guidelines..."
                      onChange={onToggleBooleanProperty('useInstruction')}
                    />
                    {useInstructionValue && (
                      <Box
                        css={{
                          paddingLeft: '$3',
                          marginBottom: '$2',
                          marginTop: '-$1',
                        }}
                      >
                        <StaticInput
                          size="small"
                          value={instructionValue}
                          placeholder="Provide instructions on how the chatbot should respond when this scenario is triggered"
                          onClick={() =>
                            setIsInstructionsValidationModalOpen(true)
                          }
                        />
                      </Box>
                    )}
                    <Checkbox
                      checked={useExitValue}
                      label="Exit via offramp"
                      onChange={onToggleBooleanProperty('useExit')}
                    />
                    {useExitValue && (
                      <Flex
                        direction="column"
                        gap={0}
                        css={{ paddingLeft: '$3' }}
                      >
                        <Select
                          label=""
                          hideLabel
                          onChange={(newVal) =>
                            onSelectChange('exit')(
                              (newVal as { label: string; value: string })
                                ?.value
                            )
                          }
                          value={OFFRAMP_OPTIONS.find(
                            (attr) => exitValue === attr.value
                          )}
                          options={OFFRAMP_OPTIONS}
                        />
                        <Text css={{ marginTop: '$2' }}>
                          Before exiting the node, confirm with the site visitor
                          using this question:
                        </Text>
                        <StaticInput
                          size="small"
                          value={exitQuestionValue}
                          placeholder="Looks like you’re interested in learning more. Would you like me to connect you with someone in sales?"
                          onClick={() => setIsExitValidationModalOpen(true)}
                          quoted
                        />
                        {!!editedScenario?.exitQuestion && (
                          <Flex
                            direction="row"
                            gap={0}
                            css={{ marginTop: '$2' }}
                          >
                            <Icon
                              color="blue7"
                              icon={faCircleInfo}
                              css={{ margin: '$half $1 0 0' }}
                            />
                            <Text css={{ color: '$blue7' }}>
                              <strong>
                                This confirmation question will be updated in
                                all playbooks where the AI responses skill is
                                used.{' '}
                              </strong>
                              We recommend reviewing your playbooks to ensure
                              that the conversation flows as expected.
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Card.Content>
              </CustomCard>
            </>
          )}
        </Flex>
      </Flex>
      <SaveConfirmationModal
        isOpen={isSaveConfirmationModalOpen}
        action="save"
        onClose={() => setIsSaveConfirmationModalOpen(false)}
        onAction={onSave}
        disabled={isAtScenariosLimit}
        disabledMessage={`You’ve reached your max of 3 scenarios.`}
      />
      <ConfirmationModal
        isOpen={!!confirmationModalOptions}
        action={confirmationModalOptions?.action}
        onClose={() => setConfirmationModalOptions(undefined)}
        onAction={confirmationModalAction}
      />
      <ValidationModal
        isOpen={isDescriptionValidationModalOpen}
        onClose={() => setIsDescriptionValidationModalOpen(false)}
        onAction={onEditDescription}
        savedValidaton={scenario?.prompt}
        type="description"
        defaultValidation={templateDescription}
      />
      <ValidationModal
        isOpen={isInstructionsValidationModalOpen}
        onClose={() => setIsInstructionsValidationModalOpen(false)}
        onAction={onEditInstruction}
        savedValidaton={scenario?.instruction}
        type="instructions"
      />
      <ValidationModal
        isOpen={isExitValidationModalOpen}
        onClose={() => setIsExitValidationModalOpen(false)}
        onAction={onEditStringProperty('exitQuestion')}
        savedValidaton={scenario?.exitQuestion}
        type="exit"
      />
    </>
  )
}

export default ScenariosEditor
